import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { AxiosError } from "axios";

import { SwitchCase } from "@enkor/toss-slash";
import { useAnimationControls } from "@enkor/framer-motion";

import { ErrorResponseType } from "@models/client";
import useModalState from "@logics/hooks/useModalState";
import WebViewBridge from "@logics/utils/webViewBridge";
import StackLayout from "@domains/common/views/layouts/StackLayout/StackLayout";
import {
  useFetchEventDetailQuery,
  useFetchHostReviewListQuery,
} from "@domains/event/network/eventQueries";
import { useAuthSession } from "@domains/auth/hooks/useAuthSession";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { CTA_TYPE } from "@domains/event/constants/event";
import { CtaType } from "@domains/event/types/event";
import { ArrowRightIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import { useRemoteConfigFlags } from "@logics/hooks/useRemoteConfigFlags";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";

import EventDetailButton from "../components/EventDetailButton/EventDetailButton";
import HostMenuBottomSheet from "../components/HostMenuBottomSheet/HostMenuBottomSheet";
import ImageSliderSection from "../sections/ImageSliderSection/ImageSliderSection";
import CommentSection from "../sections/CommentSection/CommentSection";
import EventDetailPlaceholder from "../components/EventDetailPlaceholder/EventDetailPlaceholder";
import AvatarListSection from "../sections/AvatarListSection/AvatarListSection";
import EventInfoSection from "../sections/EventInfoSection/EventInfoSection";
import ReviewSection from "../sections/ReviewSection/ReviewSection";
import EventMenuBottomSheet from "../components/EventMenuBottomSheet/EventMenuBottomSheet";
import Navbar from "../components/Navbar/Navbar";

import * as S from "./EventDetail.style";

interface EventDetailComponentProps {
  eventId: string;
}

const EventDetailComponent = ({ eventId }: EventDetailComponentProps) => {
  const { push, query } = useRouter();

  const ctaTypeRef = useRef<CtaType>(CTA_TYPE.UNJOINED);
  const [ctaType, setCtaType] = useState<CtaType>(CTA_TYPE.UNJOINED);

  const { user, guardRoute } = useAuthSession();
  const { openModal } = useModalState();

  const {
    data: eventDetail,
    status,
    error,
  } = useFetchEventDetailQuery(eventId);
  const { data: hostReviewList } = useFetchHostReviewListQuery(eventId);

  const projectNoticeControls = useAnimationControls();

  const { projectFeatureEnabled } = useRemoteConfigFlags();

  useEffect(() => {
    projectNoticeControls.start({ y: ["-100%", "0"] });
  }, [projectNoticeControls]);

  const isHostMyself = eventDetail?.host.id === user?.id;
  const hasJoined = !!eventDetail?.participantList?.find(
    (participant) => participant.participantId === user?.id,
  );

  const handleClickHostMenuButton = () =>
    guardRoute(() => {
      openModal({
        CustomComponent: (
          <HostMenuBottomSheet
            eventId={eventId}
            isEventClosed={!!eventDetail?.isClosed}
          />
        ),
      });
    });

  const handleClickOtherMenuButton = () =>
    guardRoute(() => {
      openModal({
        CustomComponent: (
          <EventMenuBottomSheet targetEventId={Number(eventId)} />
        ),
      });
    });

  const handleClickProjectDetailLink = () => {
    if (!eventDetail) return;

    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_EDITION_INFO,
    });

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToProjectDetail(
        eventDetail.project.id,
      );

      return;
    }

    push({
      pathname: NAVIGATION_ROUTE.PROJECT_DETAIL.PATHNAME,
      query: {
        ...query,
        projectId: eventDetail.project.id,
      },
    });
  };

  useEffect(() => {
    const defineCtaType = () => {
      let newCtaType: CtaType = CTA_TYPE.UNJOINED;

      if (!eventDetail) {
        newCtaType = CTA_TYPE.DEFAULT;
      } else if (isHostMyself) {
        newCtaType = eventDetail?.isClosed
          ? CTA_TYPE.HOST_CLOSED
          : CTA_TYPE.HOST;
      } else if (eventDetail?.isClosed) {
        newCtaType = hasJoined ? CTA_TYPE.JOINED_CLOSED : CTA_TYPE.CLOSED;
      } else if (hasJoined) {
        newCtaType = CTA_TYPE.JOINED;
      } else if (eventDetail?.isFull) {
        newCtaType = CTA_TYPE.FULL;
      }

      if (eventDetail?.isPartOfProject) {
        if (eventDetail?.project?.isMember) {
          newCtaType = hasJoined
            ? CTA_TYPE.PROJECT_JOINED_EVENT_JOINED
            : CTA_TYPE.PROJECT_JOINED_EVENT_UNJOINED;
        } else {
          newCtaType = CTA_TYPE.PROJECT_UNJOINED_EVENT_UNJOINED;
        }
      }

      // NOTE: 이전 CTA 타입과 다를 경우에만 업데이트
      if (ctaTypeRef.current !== newCtaType) {
        ctaTypeRef.current = newCtaType;
        setCtaType(newCtaType);
      }
    };

    defineCtaType();
  }, [eventDetail, isHostMyself, hasJoined]);

  useEffect(() => {
    if (status !== "error") return;

    const { response } = error as AxiosError<ErrorResponseType>;
    if (response?.data.errorCode !== "EV006") return;

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToMain();
    } else {
      push(NAVIGATION_ROUTE.EVENT_LIST.HREF);
    }
  }, [status]);

  return (
    <StackLayout hideNavbar>
      <Navbar
        handleClickDotMenuButton={
          isHostMyself ? handleClickHostMenuButton : handleClickOtherMenuButton
        }
      />

      <SwitchCase
        value={status}
        caseBy={{
          pending: <EventDetailPlaceholder />,
          success: eventDetail && (
            <S.SectionContainer>
              <ImageSliderSection />
              {projectFeatureEnabled && eventDetail.isPartOfProject && (
                <S.ProjectNotice
                  type="button"
                  animate={projectNoticeControls}
                  transition={{
                    delay: 0.3,
                    duration: 0.7,
                    ease: "easeInOut",
                  }}
                  onClick={handleClickProjectDetailLink}
                >
                  <S.ProjectNoticeTitle name="h6">
                    Only edition applicants can join this event
                  </S.ProjectNoticeTitle>
                  <S.ProjectNoticeContent>
                    <S.ProjectLink name="strongS">
                      View edition details
                    </S.ProjectLink>
                    <ArrowRightIcon size={20} strokeColor={grayScale.white} />
                  </S.ProjectNoticeContent>
                </S.ProjectNotice>
              )}
              <EventInfoSection eventDetail={eventDetail} />
              {hostReviewList?.shouldShowReview && (
                <>
                  <S.CustomDivider />
                  <ReviewSection
                    userId={String(eventDetail.host.id)}
                    eventId={eventId}
                  />
                </>
              )}
              <AvatarListSection
                hostId={eventDetail.host.id}
                participant={eventDetail.participant}
              />
              <CommentSection
                isHostMyself={isHostMyself}
                hasJoined={hasJoined}
              />
            </S.SectionContainer>
          ),
        }}
      />

      {eventDetail && (
        <EventDetailButton
          eventId={eventId}
          eventInfo={eventDetail}
          chatId={eventDetail.chatId}
          hostInfo={eventDetail.host}
          ctaType={ctaType}
          isEventClosed={eventDetail?.isClosed}
        />
      )}
    </StackLayout>
  );
};

export default EventDetailComponent;
